import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { notification } from 'antd';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import IconButton from '@mui/material/IconButton';
import { LoadingOutlined } from '@ant-design/icons';
import { clsx } from 'clsx';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

import { Credit, CreditCrop, CxFarm } from '@types';
import { cxHttp, CxOptions } from '@network';
import { PolicyInsuranceForm } from './PolicyInsuranceForm';
import { useRequest } from '@hooks';
import jsonExport from 'jsonexport/dist';
import { downloadCSV } from 'react-admin';
import { dateFormatter } from '@utils';

interface Props {
  credit: Credit;
  shortEdit?: boolean;
  editable?: boolean;
}

export const PolicyInsurances = ({ credit, shortEdit = false, editable }: Props) => {
  const creditId = credit.id;
  const address = Array.isArray(credit.user.location)
    ? credit.user.location[0]
    : null;

  const defaultInsurance = {
    key: '0',
    stateId: credit.user.stateId,
    creditCode: credit.code,
    accreditedName: credit.accreditedName,
    creditAmount: credit.bankApproval,
    municipality: address ? (address.neighbourhood || address.city) : '',
    phone: credit.user.phone || '',
    repLegal: '',
    cropVariety: '',
    contactName: '',
    contactPhone: '',
    agroCycle: '',
    cycleData: '',
    documentation: '',
    extraInfo: '',
    requestedAt: null,
    quotedAt: null,
    policyGeneratedAt: null,
  } as CreditCrop;

  const initialInsurances = credit.creditCrops.length > 0
    ? credit.creditCrops
    : [defaultInsurance];

  const swiperRef = useRef<any>(null);
  const [swiperSlide, setSwiperSlide] = useState<number>(0);
  const [updated, setUpdated] = useState(false);
  const [cxOptions, setCxOptions] = useState<CxOptions>({ crops: [], states: [] });
  const [insurances, setInsurances] = useState<CreditCrop[]>(initialInsurances);
  const [creditFarms, setCreditFarms] = useState<CxFarm[]>([]);
  const [usedFarmIds, setUsedFarmIds] = useState<number[]>([]);
  const cls = useStyles();

  if (insurances[0] && insurances[0].stateId) {
    defaultInsurance.stateId = insurances[0].stateId;
  }

  const loadFarms = async () => {
    const { farms } = await cxHttp.getCxCredit(creditId);
    setCreditFarms(farms || []);
  };

  const loadInsurances = async () => {
    const loadedInsurances = await cxHttp.getInsurances(creditId);
    let nextInsurances = loadedInsurances.map(ins => {
      ins.creditCode = ins.creditCode || defaultInsurance.creditCode;
      ins.accreditedName = ins.accreditedName || defaultInsurance.accreditedName;
      ins.creditAmount = ins.creditAmount || defaultInsurance.creditAmount;
      ins.municipality = ins.municipality || defaultInsurance.municipality;
      ins.stateId = ins.stateId || defaultInsurance.stateId;
      ins.phone = ins.phone || defaultInsurance.phone || '';
      return ins;
    });
    if (nextInsurances.length === 0) {
      nextInsurances = [defaultInsurance];
    }
    setInsurances(nextInsurances);
    await loadFarms();
  };

  const loadOptions = async () => {
    const nextOptions = await cxHttp.getOptions();
    setCxOptions(nextOptions);
  };

  useEffect(() => void loadInsurances(), [creditId]);

  useEffect(() => void loadOptions(), []);

  useEffect(() => {
    const farmIds: number[] = [];
    for (const ins of insurances) {
      for (const farm of (ins.farms ?? [])) {
        if (farm.id) farmIds.push(farm.id);
      }
    }
    setUsedFarmIds(farmIds);
  }, [insurances]);

  const { loading: isSaving, submit: saveInsurances } = useRequest(async () => {
    for (const [idx, ins] of insurances.entries()) {
      if (!ins.financedCropId && !ins.isProtectedCultivation) {
        swiperRef.current?.slideTo(idx);
        return notification.error({ message: 'Cultivo is mandatory' });
      }
      ins.farms = ins.farms ?? [];
      for (const farm of ins.farms) {
        if (farm.latitude == null || farm.latitude === '') {
          swiperRef.current?.slideTo(idx);
          return notification.error({ message: 'Ubicación Latitude is mandatory' });
        }
        if (farm.longitude == null || farm.longitude === '') {
          swiperRef.current?.slideTo(idx);
          return notification.error({ message: 'Ubicación Londitude is mandatory' });
        }
      }
    }
    const updatedInsurances = await cxHttp.updateInsurances(creditId, insurances);
    setInsurances(updatedInsurances);
    setUpdated(false);
    notification.success({ message: 'Insurance Policies are saved', duration: 5 });
    await loadFarms();
  });

  const updateInsurance = (updatedInsurance: CreditCrop) => {
    let stateId: number | null | undefined = null;
    let nextInsurances: CreditCrop[] = insurances.map((insurance) => {
      if (updatedInsurance.key === insurance.key) {
        stateId = updatedInsurance.stateId;
        return updatedInsurance;
      }
      insurance.phone = insurance.phone || defaultInsurance.phone || '';
      return insurance;
    });
    if (stateId) {
      nextInsurances = nextInsurances.map(insurance => {
        insurance.stateId = stateId;
        return insurance;
      });
    }
    setInsurances(nextInsurances);
    setUpdated(true);
  };

  const { loading: isDownloading, submit: downlodFiles } = useRequest(async () => {
    await cxHttp.downloadInsuranceFiles(credit.id, credit.code);
  });

  const { loading: isExporting, submit: exportInsurances } = useRequest(async () => {
    const data = insurances.map((insurance) => ({
      'Expediente': insurance.creditCode || '',
      'Acreditado': insurance.accreditedName || '',
      'Teléfono': insurance.phone || '',
      'Rep. Legal': insurance.repLegal || '',
      'Cultivo': cxOptions.crops.find(crop => crop.id === insurance.financedCropId)?.nameMx || '',
      'Variedad': insurance.cropVariety || '',
      'Municipio': insurance.municipality,
      'Estado': cxOptions.states.find(state => state.id === insurance.stateId)?.name || '',
      'Contacto Campo': insurance.contactName || '',
      'Tel. Contacto': insurance.contactPhone || '',
      'Ubicacion': (insurance.farms || []).map((farm, idx) =>
        `Farm #${idx + 1} Lat: ${farm.latitude} Lon: ${farm.longitude}`,
      ).join('; '),
      'Superficie total': insurance.financedHaCx ? insurance.financedHaCx + ' Ha' : '',
      'Rendimiento': insurance.expectedWeightCx ? insurance.expectedWeightCx + ' Tons/Ha' : '',
      'Fecha de Siembra': insurance.sawingDate
        ? dateFormatter.toDateSlash(insurance.sawingDate) : '',
      'Cosecha Escalonada': insurance.harvestStepwise ? 'Sí' : 'No',
      'Fecha de Cosecha': insurance.harvestDate
        ? dateFormatter.toDateSlash(insurance.harvestDate) : '',
      'Ciclo Agrícola': insurance.agroCycle || '',
      'Tecnificación': insurance.documentation || '',
      'Datos del ciclo': insurance.cycleData || '',
      'Información extra:': insurance.extraInfo || '',
      'Solicitado el': insurance.requestedAt
        ? dateFormatter.toDateSlash(insurance.requestedAt) : '',
      'Cotizado el': insurance.quotedAt
        ? dateFormatter.toDateSlash(insurance.quotedAt) : '',
      'Póliza Generada el': insurance.policyGeneratedAt
        ? dateFormatter.toDateSlash(insurance.policyGeneratedAt) : '',
    }));
    jsonExport(data, {}, (err, csv) => {
      downloadCSV(csv, `Insurance Policies ${credit.code}.csv`);
    });
  });

  const addInsurance = () => {
    if (insurances.length >= 15) {
      return notification.error({ message: 'Limit is 15' });
    }
    const newInsurance = { ...defaultInsurance, id: null, key: Date.now().toString() };
    const nextInsurances: CreditCrop[] = [...insurances, newInsurance];
    setInsurances(nextInsurances);
    setTimeout(() => void swiperRef.current?.slideTo(nextInsurances.length), 100);
    setUpdated(true);
  };

  const removeInsurance = () => {
    const currentSlideIdx = swiperRef.current?.realIndex ?? -1;
    const nextInsurances = insurances.filter((_, idx) => idx !== currentSlideIdx);
    setInsurances(nextInsurances);
    setUpdated(true);
  };

  const gotoNextSlide = () => swiperRef.current.slideNext();
  const gotoPrevSlide = () => swiperRef.current.slidePrev();

  return (
    <div className={cls.container}>
      <div className={cls.header}>
        <div className={cls.headerTitle}>Insurance Policies #{swiperSlide + 1}</div>
        <div className={cls.headerControls}>
          {shortEdit ? null : (
            <IconButton className={clsx(cls.btn, cls.btnDownload)} onClick={downlodFiles}>
              Insurance Files{isDownloading ? <LoadingOutlined /> : <DownloadIcon />}
            </IconButton>
          )}
          {shortEdit ? null : (
            <IconButton className={cls.btn} onClick={exportInsurances}>
              {isExporting ? <LoadingOutlined /> : <DownloadIcon />}
            </IconButton>
          )}
          <IconButton
            className={clsx(cls.btn, updated && cls.btnSaveUpdated, !editable && 'disabled')}
            onClick={editable ? saveInsurances : undefined}
          >
            {isSaving ? <LoadingOutlined /> : <SaveIcon />}
          </IconButton>
          {shortEdit ? null : (
            <IconButton
              className={clsx(cls.btn, cls.btnRemove)}
              onClick={editable ? removeInsurance : undefined}>
              <DeleteIcon />
            </IconButton>
          )}
          <IconButton className={cls.btn} onClick={gotoPrevSlide}>
            <LeftIcon />
          </IconButton>
          <div className={cls.headerText}>Policy #{swiperSlide + 1}</div>
          <IconButton className={cls.btn} onClick={gotoNextSlide}>
            <RightIcon />
          </IconButton>
          {shortEdit ? null : (
            <IconButton
              className={cls.btn}
              onClick={addInsurance}>+</IconButton>
          )}
        </div>
      </div>

      <Swiper
        onSwiper={(swiper: any) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper: any) => {
          setSwiperSlide(swiper.activeIndex);
        }}
        centeredSlides={true}
        spaceBetween={0}
        slidesPerView={1}
        keyboard={{ enabled: true }}
        navigation={true}
        modules={[Keyboard, Navigation, Pagination]}
        className={cls.swiper}
        pagination={{ type: 'fraction' }}
        noSwiping
        touchStartPreventDefault={false}
        simulateTouch={false}
        // alwaysShowSwipe={true}
      >
        {insurances.map((insurance, idx) => (
          <SwiperSlide key={insurance.key} virtualIndex={idx}>
            <PolicyInsuranceForm
              insurance={insurance}
              updateInsurance={updateInsurance}
              credit={credit}
              cxOptions={cxOptions}
              creditFarms={creditFarms}
              setCreditFarms={setCreditFarms}
              usedFarmIds={usedFarmIds}
              idx={idx}
              shortEdit={shortEdit}
              editable={editable}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    border: '1px solid var(--primary-color)',
    borderRadius: '8px',
    padding: '15px 25px 15px 35px',
    backgroundColor: 'var(--foreground-color)',
    color: 'var(--text-color)',
    maxWidth: 'calc(100vw - 340px)',
    maxHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    color: 'var(--primary-color)',
    fontSize: '25px',
  },
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: 2,
  },
  headerText: {
    paddingRight: 10,
    color: 'var(--text-color)',
    fontSize: 15,
    textAlign: 'center',
  },
  swiper: {
    position: 'initial',
    '& .swiper-button-prev': {
      display: 'none !important',
    },
    '& .swiper-button-next': {
      display: 'none !important',
    },
    '& .swiper-pagination': {
      right: 100,
      display: 'block !important',
    },
  },
  btn: {
    marginRight: 10,
    height: 20,
    width: 29,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'var(--primary-color) !important',
    color: 'white',
    fontSize: 12,
    borderRadius: 4,
    opacity: 0.45,
    '&:hover': {
      opacity: 1,
      boxShadow: '0 4px 8px 0 rgba(48, 55, 61, 0.2)',
    },
    '& svg': {
      minWidth: 18,
    },
  },
  btnText: {
    width: 'auto !important',
  },
  btnSaveUpdated: {
    opacity: 0.8,
    '&.disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  btnRemove: {
    backgroundColor: '#FF0000 !important',
  },
  btnDownload: {
    width: 'auto !important',
    minWidth: '120px !important',
    padding: '0 8px !important',
    '& svg': {
      marginLeft: 8,
    },
  },
});

const SaveIcon = () => (
  <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_3965_24270)">
      <path d="M14.8964 2.25077L13.2321 0.60257C13.1643 0.538906 13.075 0.5 12.9786 0.5H2.25C2.21429 0.5 2.18571 0.5 2.15 0.5C2.15 0.5 2.14643 0.5 2.14286 0.5H0.357143C0.160714 0.5 0 0.659161 0 0.85369V15.1463C0 15.3408 0.160714 15.5 0.357143 15.5H14.6429C14.8393 15.5 15 15.3408 15 15.1463V2.50189C15 2.40639 14.9607 2.31797 14.8964 2.25077ZM7.5 12.3486C6.11786 12.3486 5 11.2416 5 9.87279C5 8.50401 6.11786 7.39696 7.5 7.39696C8.88214 7.39696 10 8.50401 10 9.87279C10 11.2416 8.88214 12.3486 7.5 12.3486ZM11.4286 4.40474C11.4286 4.99187 10.95 5.46581 10.3571 5.46581H4.64286C4.05 5.46581 3.57143 4.99187 3.57143 4.40474V1.90769C3.57143 1.72377 3.72143 1.57522 3.90714 1.57522L7.91071 1.56814C8.07857 1.56814 8.21429 1.70255 8.21429 1.86878V3.97324C8.21429 4.20667 8.40357 4.39413 8.63929 4.39413H9.575C9.81071 4.39413 10 4.20667 10 3.97324V1.86171C10 1.69547 10.1357 1.56107 10.3036 1.56107H11.0929C11.2786 1.56107 11.4286 1.70962 11.4286 1.89354V4.40474Z"
        fill="#E1E5ED" />
    </g>
    <defs>
      <clipPath id="clip0_3965_24270">
        <rect width="15" height="15" fill="white" transform="translate(0 0.5)" />
      </clipPath>
    </defs>
  </svg>
);

const DeleteIcon = () => (
  <svg width="12" height="16" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.32511 2.40625C8.35111 2.51427 8.46113 2.60028 8.57314 2.60028H10.7034C11.0895 2.60028 11.4035 2.91432 11.4035 3.30037C11.4035 3.62042 11.1895 3.88845 10.8975 3.97246L10.5314 14.0258C10.5014 14.8439 9.82131 15.5 9.0012 15.5H2.40232C1.58421 15.5 0.90212 14.8419 0.872116 14.0258L0.506067 3.97246C0.214029 3.88845 0 3.61842 0 3.30037C0 2.91432 0.314042 2.60028 0.700093 2.60028H2.83038C2.94039 2.60028 3.05041 2.51227 3.07841 2.40625L3.22843 1.80617C3.41445 1.06608 4.13855 0.5 4.90065 0.5H6.50287C7.26497 0.5 7.99107 1.06608 8.17509 1.80617L8.32511 2.40625ZM5.20069 5.80071V12.6016C5.20069 12.8777 5.42472 13.1017 5.70076 13.1017C5.9768 13.1017 6.20083 12.8777 6.20083 12.6016V5.80071C6.20083 5.52467 5.9768 5.30064 5.70076 5.30064C5.42472 5.30064 5.20069 5.52467 5.20069 5.80071ZM2.90039 5.81471L3.10041 12.6156C3.10841 12.8917 3.33844 13.1097 3.61448 13.1017C3.89052 13.0937 4.10855 12.8636 4.10055 12.5876L3.90052 5.78671C3.89252 5.51067 3.66249 5.29264 3.38645 5.30064C3.11041 5.30864 2.89239 5.53867 2.90039 5.81471ZM7.501 5.7847L7.30097 12.5856C7.29297 12.8616 7.511 13.0917 7.78704 13.0997C8.06308 13.1077 8.29311 12.8897 8.30111 12.6136L8.50113 5.81271C8.50913 5.53667 8.29111 5.30664 8.01507 5.29864C7.73903 5.29064 7.509 5.50867 7.501 5.7847ZM4.57061 2.59828H6.82891C6.88292 2.59828 6.91692 2.55427 6.90492 2.50227L6.81491 2.14422C6.7849 2.0262 6.62088 1.89819 6.50087 1.89819H4.89865C4.77864 1.89819 4.61461 2.0262 4.58461 2.14422L4.4946 2.50227C4.4806 2.55627 4.5146 2.59828 4.57061 2.59828Z"
      fill="white" />
  </svg>
);

const LeftIcon = () => (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="3.10436"
      height="9.31307"
      rx="1.55218"
      transform="matrix(-0.707107 -0.707107 -0.707107 0.707107 9.0166 2.60968)"
      fill="white" />
    <rect width="3.10436"
      height="9.31307"
      rx="1.55218"
      transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 6.8291 13.5853)"
      fill="white" />
  </svg>
);

const RightIcon = () => (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.763672"
      y="2.60962"
      width="3.10436"
      height="9.31307"
      rx="1.55218"
      transform="rotate(-45 0.763672 2.60962)"
      fill="white" />
    <rect x="2.95117"
      y="13.5852"
      width="3.10436"
      height="9.31307"
      rx="1.55218"
      transform="rotate(-135 2.95117 13.5852)"
      fill="white" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="18" height="12" viewBox="0 0 18 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 5C17.5523 5 18 5.44772 18 6V11C18 11.5523 17.5523 12 17 12H1C0.447715 12 2.41598e-08 11.5523 0 11V6C0 5.44772 0.447715 5 1 5C1.55228 5 2 5.44772 2 6V10H16V6C16 5.44772 16.4477 5 17 5Z"
      fill="white" />
    <path d="M9 0C9.55228 0 10 0.447715 10 1V5.7793L11.3809 4.39844C11.7714 4.00794 12.4063 4.00897 12.7969 4.39941C13.1874 4.78994 13.1884 5.42491 12.7979 5.81543L9.89355 8.7207C9.65751 8.95675 9.33206 9.04784 9.02539 8.99805C9.01694 8.99826 9.0085 9 9 9C8.72063 9 8.46855 8.88491 8.28711 8.7002C8.28531 8.69842 8.28304 8.69711 8.28125 8.69531L5.42676 5.84082C5.03623 5.4503 5.03623 4.81728 5.42676 4.42676C5.81729 4.03631 6.45032 4.03626 6.84082 4.42676L8 5.58594V1C8 0.447715 8.44772 0 9 0Z"
      fill="white" />
  </svg>
);
